import { apiFetchWithTimeout } from './apiUtils';

export const directoryCategories = ['transferring_attorney', 'bond_attorney', 'bond_originator'] as const;
export type DirectoryCategory = typeof directoryCategories[number];
export type ProfessionalContact = { first_name: string; last_name: string; contact_number: string; email_address: string; company_name: string; directory_id?: string };
export type DirectoryEntry = ProfessionalContact & { id: string; categories: DirectoryCategory[]; active: boolean; version: number };
export const directoryCategoryLabels: Record<DirectoryCategory, string> = { transferring_attorney: 'Transferring Attorney', bond_attorney: 'Bond Attorney', bond_originator: 'Bond Originator' };

export function contactSnapshot(entry: DirectoryEntry): ProfessionalContact {
  return { first_name: entry.first_name, last_name: entry.last_name, contact_number: entry.contact_number, email_address: entry.email_address, company_name: entry.company_name, directory_id: entry.id };
}

export function completeProfessional(contact: ProfessionalContact) {
  return Boolean(contact.first_name.trim() && contact.last_name.trim() && contact.company_name.trim()
    && /^\d{7,20}$/.test(contact.contact_number.replace(/\D/g, '')) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email_address));
}

export async function directoryRequest(path: string, headers: Record<string, string>, options: RequestInit = {}) {
  const response = await apiFetchWithTimeout(`/api/professional-directory${path}`, { ...options, headers: { 'Content-Type': 'application/json', ...headers } }, 5000);
  const body = await response.json();
  if (!response.ok) throw Object.assign(new Error(body.error ?? 'Directory unavailable.'), { status: response.status, items: body.items ?? [] });
  return body;
}

export type SearchState = { items: DirectoryEntry[]; loading: boolean; searched: boolean; error: string };
export function createProfessionalSearch(fetcher: (query: string, signal: AbortSignal) => Promise<DirectoryEntry[]>, publish: (state: SearchState) => void) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  let sequence = 0;
  const cache = new Map<string, { time: number; items: DirectoryEntry[] }>();
  const stop = () => { sequence += 1; clearTimeout(timer); controller?.abort(); };
  return {
    search(value: string) {
      stop();
      const current = sequence;
      const query = value.trim().toLowerCase();
      publish({ items: [], loading: false, searched: false, error: '' });
      if (query.length < 2) return;
      const cached = cache.get(query);
      if (cached && Date.now() - cached.time < 30000) { publish({ items: cached.items, loading: false, searched: true, error: '' }); return; }
      timer = setTimeout(async () => {
        controller = new AbortController();
        publish({ items: [], loading: true, searched: false, error: '' });
        try {
          const items = (await fetcher(query, controller.signal)).slice(0, 10);
          if (current !== sequence) return;
          if (cache.size >= 40) cache.delete(cache.keys().next().value!);
          cache.set(query, { time: Date.now(), items });
          publish({ items, loading: false, searched: true, error: '' });
        } catch (error) {
          if (current !== sequence) return;
          publish({ items: [], loading: false, searched: true, error: error instanceof Error ? error.message : 'Directory unavailable.' });
        }
      }, 225);
    },
    clearCache() { cache.clear(); },
    dispose: stop,
  };
}
